import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useMemo,
} from "react";

import { type DialogSteps, useDialogSteps } from "ui/dialog";
import { PARENT_TOUCHPOINT_STEPS } from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal.constants";

import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import { CampaignAttributes, useCurrentCampaign } from "hooks/api/use-campaigns";

import { useClientCampaignsInModal } from "hooks/use-client-campaigns";

import { PaginatedRequestOptions } from "types/pagination";

const CAMPAIGN_REQUEST_OPTIONS: PaginatedRequestOptions = {
  unpaged: true,
  sort: "name",
};

interface ParentTouchpointContextType extends DialogSteps {
  // Modal state
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedVersion: TouchpointVersionAttributes | undefined;
  setSelectedVersion: (version: TouchpointVersionAttributes | undefined) => void;

  // Core data
  currentClient: any;
  defaultCampaign: CampaignAttributes;
  currentTouchpoint: any;
  clientCampaigns: CampaignAttributes[];

  // Hook state
  selectedCampaign: CampaignAttributes;
  setSelectedCampaign: (campaign: CampaignAttributes) => void;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  generatedEmails: Array<{ html: string; beeJson: string }>;
  setGeneratedEmails: Dispatch<SetStateAction<Array<{ html: string; beeJson: string }>>>;
  currentEmailIndex: number;
  setCurrentEmailIndex: (index: number) => void;
  isTransitioning: boolean;
  setIsTransitioning: (transitioning: boolean) => void;
  generateError: boolean;
  setGenerateError: (value: boolean) => void;
  progressText: string;
  setProgressText: (text: string) => void;

  /** Shared across all step components so generation is not aborted when stepping from 2 → 3 */
  abortControllerRef: RefObject<AbortController | null>;
}

const ParentTouchpointContext = createContext<ParentTouchpointContextType | undefined>(undefined);

interface ParentTouchpointContextProviderProps {
  children: ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const ParentTouchpointContextProvider = ({
  children,
  isOpen,
  setIsOpen,
}: ParentTouchpointContextProviderProps) => {
  // Get core data
  const currentClient = useCurrentClient();
  const defaultCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  // Modal state - use parent's state directly
  const [selectedVersion, setSelectedVersion] = useState<TouchpointVersionAttributes | undefined>(
    undefined,
  );
  const steps = useDialogSteps({ total: PARENT_TOUCHPOINT_STEPS });

  // Hook state
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignAttributes>(defaultCampaign);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmails, setGeneratedEmails] = useState<Array<{ html: string; beeJson: string }>>(
    [],
  );
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [generateError, setGenerateError] = useState(false);
  const [progressText, setProgressText] = useState("");

  const abortControllerRef = useRef<AbortController | null>(null);

  // Abort in-flight generation when modal closes (or provider unmounts)
  useEffect(() => {
    if (!isOpen && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Fetch client campaigns once at context level
  const clientCampaigns = useClientCampaignsInModal(isOpen, CAMPAIGN_REQUEST_OPTIONS);

  /* `steps`' callbacks use functional updates, so the memo only needs `steps.step`. */
  const value = useMemo(
    () => ({
      // Modal state
      isOpen,
      setIsOpen,
      selectedVersion,
      setSelectedVersion,
      ...steps,

      // Core data
      currentClient,
      defaultCampaign,
      currentTouchpoint,
      clientCampaigns,

      // Hook state
      selectedCampaign,
      setSelectedCampaign,
      isGenerating,
      setIsGenerating,
      generatedEmails,
      setGeneratedEmails,
      currentEmailIndex,
      setCurrentEmailIndex,
      isTransitioning,
      setIsTransitioning,
      generateError,
      setGenerateError,
      progressText,
      setProgressText,
      abortControllerRef,
    }),
    [
      isOpen,
      setIsOpen,
      selectedVersion,
      steps.step,
      currentClient,
      defaultCampaign,
      currentTouchpoint,
      clientCampaigns,
      selectedCampaign,
      isGenerating,
      generatedEmails,
      currentEmailIndex,
      isTransitioning,
      generateError,
      progressText,
    ],
  );

  return (
    <ParentTouchpointContext.Provider value={value}>{children}</ParentTouchpointContext.Provider>
  );
};

export const useParentTouchpointContext = () => {
  const context = useContext(ParentTouchpointContext);
  if (context === undefined) {
    throw new Error(
      "useParentTouchpointContext must be used within a ParentTouchpointContextProvider",
    );
  }
  return context;
};
